import type { QueryCacheUserChangeEvent, QueryCacheUserServiceDataSource } from './index';
import { replacer, reviver } from '@thinkalpha/common/serialization/serializer.js';

export const QUERY_CACHE_USER_SERVICE_WORKER_MESSAGE_TYPE = 'query-cache-user-service:user-change';

export interface QueryCacheUserServiceWorkerMessage {
    readonly type: typeof QUERY_CACHE_USER_SERVICE_WORKER_MESSAGE_TYPE;
    readonly event: QueryCacheUserChangeEvent;
}

export class PushQueryCacheUserServiceDataSource implements QueryCacheUserServiceDataSource {
    readonly #callbacks = new Set<(event: QueryCacheUserChangeEvent) => void>();

    subscribe(callback: (event: QueryCacheUserChangeEvent) => void): () => void {
        this.#callbacks.add(callback);
        return () => {
            this.#callbacks.delete(callback);
        };
    }

    notify(event: QueryCacheUserChangeEvent): void {
        for (const callback of this.#callbacks) {
            try {
                callback(event);
            } catch {
                // Keep fan-out resilient: one bad subscriber must not block others.
            }
        }
    }
}

export function createQueryCacheUserServiceWorkerMessage(
    event: QueryCacheUserChangeEvent,
): QueryCacheUserServiceWorkerMessage {
    return {
        type: QUERY_CACHE_USER_SERVICE_WORKER_MESSAGE_TYPE,
        event,
    };
}

export function bridgeQueryCacheUserServiceDataSourceToWorker(
    dataSource: QueryCacheUserServiceDataSource,
    worker: Pick<Worker, 'postMessage'>,
): () => void {
    return dataSource.subscribe((event) => {
        worker.postMessage(JSON.stringify(createQueryCacheUserServiceWorkerMessage(event), replacer));
    });
}

export function parseQueryCacheUserServiceWorkerMessage(data: unknown): QueryCacheUserServiceWorkerMessage | undefined {
    const parsed = parseMaybeSerializedMessage(data);
    if (!isRecord(parsed) || parsed.type !== QUERY_CACHE_USER_SERVICE_WORKER_MESSAGE_TYPE) return undefined;

    const event = parseQueryCacheUserChangeEvent(parsed.event);
    if (!event) return undefined;

    return createQueryCacheUserServiceWorkerMessage(event);
}

function parseMaybeSerializedMessage(data: unknown): unknown {
    if (typeof data !== 'string') return data;

    try {
        return JSON.parse(data, reviver);
    } catch {
        return undefined;
    }
}

function parseQueryCacheUserChangeEvent(value: unknown): QueryCacheUserChangeEvent | undefined {
    if (!isRecord(value)) return undefined;

    const kind = value.kind;
    if (kind !== 'identity' && kind !== 'entitlements') return undefined;

    const effectiveUserId = value.effectiveUserId;
    if (effectiveUserId !== undefined && typeof effectiveUserId !== 'string') return undefined;

    const fingerprint = value.fingerprint;
    if (fingerprint !== undefined && typeof fingerprint !== 'string') return undefined;

    return {
        kind,
        effectiveUserId,
        fingerprint,
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
